import { FC, PropsWithChildren } from 'react'

import { useLocation } from 'react-router-dom'
import { styled } from 'styled-components'

import ErrorBoundary from 'core/components/lib/ErrorBoundary'
import type { FallbackProps } from 'core/components/lib/ErrorBoundary/defaultErrorBoundaries'
import {
  fontSizeMeta,
  fontSizeTitle,
  fontWeightSemibold,
  radiusCard,
  spaceCardPadding,
} from 'core/styles'
import { error as errorToken } from 'core/styles/tokens'

const describeError = (error: unknown): string => {
  if (typeof error !== 'object' || error === null) return 'Unexpected error'

  const { message, status } = error as { message?: unknown; status?: unknown }
  const messageText = typeof message === 'string' ? message.trim() : ''
  const statusText = typeof status === 'number' ? String(status) : ''

  if (statusText && messageText) return `${statusText} — ${messageText}`
  return statusText || messageText || 'Unexpected error'
}

// Rendering a fallback that throws escapes its own boundary, so this component
// reads no store and calls no hook.
const ResourceErrorFallback: FC<FallbackProps> = ({ className, error }) => (
  <Box className={className}>
    <BoxTitle>This page could not be displayed</BoxTitle>
    <BoxMessage>{describeError(error)}</BoxMessage>
    <BoxHint>
      Go back to the previous page or navigate to a different page using the
      menu on the left.
    </BoxHint>
  </Box>
)

const ResourceErrorBoundary: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation()

  return (
    <ErrorBoundary
      ErrorFallback={ResourceErrorFallback}
      ReportErrorFallback={ResourceErrorFallback}
      ident='ResourceErrorBoundary'
      // A caught boundary stops re-rendering its subtree, so without a remount
      // the fallback survives every later navigation.
      key={pathname}
    >
      {children}
    </ErrorBoundary>
  )
}

export default ResourceErrorBoundary

const Box = styled.div`
  margin: 16px;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${radiusCard};
  background: ${(p) => p.theme.offset};
  padding: ${spaceCardPadding};
  max-width: 800px;
  color: ${(p) => p.theme.text};
`

const BoxTitle = styled.div`
  margin-bottom: 16px;
  color: ${errorToken};
  font-size: ${fontSizeTitle};
  font-weight: ${fontWeightSemibold};
`

const BoxMessage = styled.div`
  margin-bottom: 8px;
  word-break: break-word;
  font-size: ${fontSizeMeta};
`

const BoxHint = styled.div`
  margin-top: 16px;
  color: ${(p) => p.theme.labelText};
  font-size: ${fontSizeMeta};
`
